import { FC, memo, useMemo } from 'react'

import { useFragment } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Link } from '@factorialco/f0-react'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { ExpensesExpenseExpensableApprovableFragmentDoc } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import Attachments from 'modules/inbox_tasks/components/Attachments'
import { cleanDocuments } from 'modules/inbox_tasks/tasks/approval/ExpensesExpensable/helpers'
import { ExpensableTypeComponentProps } from 'modules/inbox_tasks/tasks/approval/ExpensesExpensable/types'

const ExpenseExpensableDetails: FC<ExpensableTypeComponentProps> = ({
  id,
  info,
  moreDetails,
  commonDetails,
  description,
}) => {
  const { data } = useFragment({
    fragment: ExpensesExpenseExpensableApprovableFragmentDoc,
    fragmentName: 'ExpensesExpenseExpensableApprovable',
    from: { __typename: 'ExpensesExpensable', id },
  })

  const expense = data?.expense

  const details = useMemo(
    () =>
      [
        ...commonDetails,
        {
          title: i18n.t('expenses_v2.forms.fields.category.label'),
          content: {
            type: 'item' as const,
            text: expense?.category?.name || '-',
          },
        },
        {
          title: i18n.t('dashboard.todo.approvals.expenses.vendor_name'),
          content: {
            type: 'item' as const,
            text: expense?.merchantName || '-',
          },
        },
        info.amount && {
          title: i18n.t('expenses_v2.forms.fields.amount.label'),
          content: {
            type: 'item' as const,
            text: info.amount,
          },
        },
        info.reimbursableAmount && {
          title: i18n.t('expenses_v2.forms.fields.reimbursable_amount.label'),
          content: {
            type: 'item' as const,
            text: info.reimbursableAmount,
          },
        },
        {
          title: i18n.t('expenses_v2.forms.fields.effective_on.label'),
          content: {
            type: 'item' as const,
            text: info.effectiveOn,
          },
        },
        description && {
          title: i18n.t('expenses_v2.forms.fields.description.label'),
          content: {
            type: 'item' as const,
            text: description,
          },
        },
      ].filter(Boolean),
    [commonDetails, expense, info, description]
  )

  const files = useMemo(() => {
    let res = !!expense?.files ? cleanDocuments(expense.files) : []

    const signedDocument = expense?.signedDocument

    if (signedDocument?.file) {
      res = [...res, ...cleanDocuments([signedDocument.file], true)]
    } else if (signedDocument?.originalFiles) {
      res = [...res, ...cleanDocuments(signedDocument.originalFiles, true)]
    }

    return res
  }, [expense])

  return (
    <>
      <DetailsItemsList details={details} tableView />
      <Box alignItems="flexStart" paddingBottom="s4">
        <F0Link href={moreDetails.url}>{moreDetails.title}</F0Link>
      </Box>
      {!!files && !!files.length && (
        <Box flexDirection="column" gap="s16">
          <Text.Header3>
            {i18n.t('expenses_v2.confirmation_screen.expense.attached_documents_block.title')}
          </Text.Header3>
          <Attachments files={files} />
        </Box>
      )}
    </>
  )
}

export default memo(ExpenseExpensableDetails)
